/* eslint-disable react/prop-types */
import React, { FC, useEffect, useState } from 'react';
import {
    Icon,
    SelectionMode,
    DetailsListLayoutMode,
    IconButton,
} from '@fluentui/react';
import { Language, DetailsList, ColumnInfo } from '@eta/components';
import { PermissionClaim } from '@eta/components/dist/authentication/Claim';
import { useTranslation } from 'react-i18next';
import { GetRepresentationOfTaxpayerDto } from '../../../Services/ClientProxy/clientProxy';
import { Icons, Actions, Languages } from '../../../common/config/constants';
import './ERPGrid.scss';
import { UIHelpers, NumberOfShimmerLines } from '../../../helpers/UIHelpers';
import { ClaimsHelper } from '../../../helpers/ClaimsHelper';

interface ERPGridProps {
    currentLanguage: Language;
    items: GetRepresentationOfTaxpayerDto[];
    onActionClick: (
        action: string,
        item: GetRepresentationOfTaxpayerDto
    ) => void;
}

export const ERPGrid: FC<ERPGridProps> = (props: ERPGridProps) => {
    const hasMngERPPerm = ClaimsHelper.hasPermission(PermissionClaim.MngERP);
    const { t } = useTranslation(['resources', 'myProfile', 'common']);
    const [columns, setColumns] = useState<ColumnInfo[]>();
    const {
        currentLanguage: currentLanguageProps,
        items: itemsProps,
        onActionClick,
    } = props;

    const [currentLanguage, setCurrentLanguage] = useState<Language>(
        currentLanguageProps
    );
    const APIKey = 'APIKey';
    const [items, setItems] = useState<GetRepresentationOfTaxpayerDto[]>();

    useEffect(() => {
        setItems(itemsProps?.slice());
    }, [itemsProps]);

    useEffect(() => {
        if (currentLanguage.lang !== currentLanguageProps.lang) {
            setCurrentLanguage(currentLanguageProps);

            setItems(itemsProps?.slice());
        }
    }, [currentLanguageProps, itemsProps, currentLanguage]);

    useEffect(() => {
        const gridColumns = [
            {
                key: 'profileNamePrimaryLang',
                name: t('form.erp.Name'),
                fieldName: 'profileNamePrimaryLang',
                minWidth: 100,
                maxWidth: 250,
                isResizable: true,

                isSortedDescending: true,

                onRender: (item: GetRepresentationOfTaxpayerDto) => {
                    return (
                        <div className="horizontal">
                            <Icon
                                iconName={
                                    item.isMainTaxpayerAccountingSystem
                                        ? 'FavoriteStarFill'
                                        : 'System'
                                }
                            />
                            <span>{currentLanguage?.lang.toLocaleLowerCase().startsWith(Languages.En)?item.profileNamePrimaryLang:item.profileNameSecondaryLang}</span>
                        </div>
                    );
                },
            },
            {
                key: 'clientID',
                name: t('form.erp.ClientID'),
                fieldName: 'clientID',
                minWidth: 110,
                maxWidth: 200,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,
            },
            {
                key: 'clientValidTo',
                name: t('form.erp.ClientSecretexpiration'),
                fieldName: 'clientValidTo',
                minWidth: 100,
                maxWidth: 200,
                isRowHeader: true,
                isResizable: true,
                isSorted: false,
                isSortedDescending: false,
                sortAscendingAriaLabel: 'Sorted A to Z',
                sortDescendingAriaLabel: 'Sorted Z to A',
                data: 'string',
                isPadded: true,

                onRender: (item: GetRepresentationOfTaxpayerDto) => {
                    return (
                        <span>
                            {item.clientValidTo
                                ? UIHelpers.getLocalizedDateTime('Date', item.clientValidTo, currentLanguageProps.lang)
                                : ''}
                        </span>
                    );
                },
            },
            {
                key: 'callbackUrl',
                name: t('form.Notification.NotificationURL'),
                fieldName: 'callbackUrl',
                minWidth: 150,
                maxWidth: 150,
                isRowHeader: true,
                isResizable: true,
            },
            {
                key: 'apiKey',
                name: t('form.erp.APIKey'),
                fieldName: 'apiKey',
                minWidth: 150,
                maxWidth: 150,
                isRowHeader: true,
                isResizable: true,

                onRender: (item: GetRepresentationOfTaxpayerDto) => {
                    return <span>{item.apiKey ? APIKey.replace(/./g, '*'):item.apiKey}</span>;
                },
            },
            {
                key: 'action',
                name: '',
                fieldName: '',
                minWidth: 100,
                maxWidth: 100,
                isRowHeader: true,
                isResizable: false,

                onRender: (item: GetRepresentationOfTaxpayerDto) => {
                    return hasMngERPPerm ? (
                        <span>
                            <IconButton
                                className="iconActions"
                                iconProps={{ iconName: Icons.MoreVertical }}
                                menuProps={{
                                    items: [
                                        {
                                            key: 'Edit',
                                            text: t('EditERP'),
                                            onClick: () =>
                                                onActionClick &&
                                                onActionClick(
                                                    Actions.EditERP,
                                                    item
                                                ),
                                        },
                                        {
                                            key: 'RegenerateSecrets',
                                            text: t('RegenerateSecrets'),
                                            onClick: () =>
                                                onActionClick &&
                                                onActionClick(
                                                    Actions.RegenerateSecrets,
                                                    item
                                                ),
                                        },
                                    ],
                                }}
                            />
                        </span>
                    ) : null;
                },
            },
        ];
        setColumns(gridColumns);
    }, [currentLanguageProps, onActionClick, t]);

    return (
        <DetailsList
            className="grid erpGrid"
            columns={columns ?? []}
            items={items}
            selectionMode={SelectionMode.none}
            layoutMode={DetailsListLayoutMode.justified}
            shimmerLines={NumberOfShimmerLines}
            enableShimmer={!items}
            noItemsPlaceholder={<span>{t('common:NoRecords')} </span>}
            totalItemCount={items?.length}
            labels={{
                resultPerPage: t('common:nbItemPerPage'),
                totalRecord: t('common:totalRecord'),
            }}
            paginationSetting={{
                nbItemPerPage: 10,
                nbPageShown: 3 /* currentPage: 0, totalPage: 20 when managed externaly */,
            }}
        />
    );
};
